import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Navigate, Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { useAuth } from '../../hooks/useAuth';
import { PageLoader } from '../ui/LoadingSpinner';
import { SubscriptionBanner } from '../billing/SubscriptionBanner';
/**
 * AppLayout — wraps all authenticated app pages.
 *
 * Gate order:
 *   1. Clerk not yet initialised → show full-page loader (avoids flash redirect)
 *   2. Not signed in → redirect to /login
 *   3. Signed in but onboarding incomplete → redirect to /onboarding
 *   4. Fully onboarded → render the page
 */
export function AppLayout() {
    const { isLoaded, isAuthenticated, profileComplete, activeAccount } = useAuth();
    if (!isLoaded)
        return _jsx(PageLoader, {});
    if (!isAuthenticated)
        return _jsx(Navigate, { to: "/login", replace: true });
    if (!profileComplete)
        return _jsx(Navigate, { to: "/onboarding", replace: true });
    return (_jsxs("div", { className: "flex h-full", children: [_jsx(Sidebar, {}), _jsxs("div", { className: "flex flex-1 flex-col min-w-0 overflow-hidden", children: [_jsx(TopBar, {}), _jsxs("main", { className: "flex-1 overflow-y-auto", children: [_jsx(SubscriptionBanner, { organisationId: activeAccount?.organisationId ?? undefined }), _jsx("div", { className: "p-6", children: _jsx(Outlet, {}) })] })] })] }));
}
/**
 * PrintLayout — wraps print/export pages that must be auth-gated but should
 * render with NO sidebar, topbar, or any other chrome.  The only thing this
 * layout does is redirect unauthenticated users away; everything else is a
 * plain white page so `window.print()` (and browser "Save as PDF") produces
 * a clean document.
 */
export function PrintLayout() {
    const { isLoaded, isAuthenticated, profileComplete } = useAuth();
    if (!isLoaded)
        return _jsx(PageLoader, {});
    if (!isAuthenticated)
        return _jsx(Navigate, { to: "/login", replace: true });
    if (!profileComplete)
        return _jsx(Navigate, { to: "/onboarding", replace: true });
    return _jsx(Outlet, {});
}
/**
 * AuthLayout — wraps the sign-in / sign-up / forgot-password pages.
 *
 * Redirects signed-in users away so they don't land on /login after refresh.
 */
export function AuthLayout() {
    const { isLoaded, isAuthenticated, profileComplete } = useAuth();
    if (!isLoaded)
        return _jsx(PageLoader, {});
    // Fully onboarded → straight to the app
    if (isAuthenticated && profileComplete)
        return _jsx(Navigate, { to: "/dashboard", replace: true });
    // Signed in but still needs onboarding
    if (isAuthenticated && !profileComplete)
        return _jsx(Navigate, { to: "/onboarding", replace: true });
    return (_jsx("div", { className: "min-h-full flex items-center justify-center bg-gray-50 py-12 px-4", children: _jsxs("div", { className: "w-full max-w-md", children: [_jsxs("div", { className: "text-center mb-8", children: [_jsx("div", { className: "inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-wine-700 mb-4", children: _jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "white", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "h-8 w-8", children: _jsx("path", { d: "M8 22h8M12 11v11M3 7c0 5.25 3.75 8 9 8s9-2.75 9-8L18 2H6L3 7z" }) }) }), _jsx("h1", { className: "text-2xl font-bold text-gray-900", children: "CellarManager" }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: "Wine collection management" })] }), _jsx("div", { className: "flex justify-center", children: _jsx(Outlet, {}) })] }) }));
}
